import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import FormFieldWithPrivacy from './FormFieldWithPrivacy';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const personalInfoSchema = z.object({
  fullName: z.string().min(2, { message: 'Full name is required' }),
  gender: z.enum(['male', 'female', 'other'], { required_error: 'Gender is required' }),
  dateOfBirth: z.string({ required_error: 'Date of birth is required' }),
  heightCm: z.string().optional(),
  weightKg: z.string().optional(),
  displayEmail: z.string().optional(),
  residenceCity: z.string().optional(),
  residenceCountry: z.string().optional(),
  religion: z.string().optional(),
  casteOrSect: z.string().optional(),
  motherTongue: z.string().optional(),
  aboutMe: z.string().optional(),
});

export type EnhancedPersonalInfoFormValues = z.infer<typeof personalInfoSchema>;

interface EnhancedPersonalInfoFormProps {
  defaultValues?: Partial<EnhancedPersonalInfoFormValues>;
  defaultPrivacySettings?: Record<string, boolean>;
  onSubmit: (values: EnhancedPersonalInfoFormValues, privacySettings: Record<string, boolean>) => void;
  isSubmitting?: boolean;
}

const EnhancedPersonalInfoForm: React.FC<EnhancedPersonalInfoFormProps> = ({
  defaultValues,
  defaultPrivacySettings = {},
  onSubmit,
  isSubmitting = false,
}) => {
  const [privacySettings, setPrivacySettings] = useState<Record<string, boolean>>(defaultPrivacySettings);

  const form = useForm<EnhancedPersonalInfoFormValues>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      fullName: '',
      gender: 'male',
      dateOfBirth: '',
      heightCm: '',
      weightKg: '',
      displayEmail: '',
      residenceCity: '',
      residenceCountry: '',
      religion: '',
      casteOrSect: '',
      motherTongue: '',
      aboutMe: '',
      ...defaultValues,
    },
  });

  const handlePrivacyChange = (fieldName: string, isPrivate: boolean) => {
    setPrivacySettings(prev => ({
      ...prev,
      [fieldName]: isPrivate
    }));
  };

  const handleFormSubmit = (values: EnhancedPersonalInfoFormValues) => {
    onSubmit(values, privacySettings);
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];



  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Personal Details</CardTitle>
            <p className="text-sm text-muted-foreground">
              Use the privacy toggles to control which information is public or private
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithPrivacy
                control={form.control}
                name="fullName"
                label="Full Name"
                required
                isPrivate={privacySettings.fullName || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Enter your full name"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="gender"
                label="Gender"
                type="select"
                options={genderOptions}
                required
                isPrivate={privacySettings.gender || false}
                onPrivacyChange={handlePrivacyChange}
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="dateOfBirth"
                label="Date of Birth"
                required
                isPrivate={privacySettings.dateOfBirth || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="YYYY-MM-DD"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="displayEmail"
                label="Display Email"
                isPrivate={privacySettings.displayEmail || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="your@email.com"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Physical Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormFieldWithPrivacy
                control={form.control}
                name="heightCm"
                label="Height (cm)"
                isPrivate={privacySettings.heightCm || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="170"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="weightKg"
                label="Weight (kg)"
                isPrivate={privacySettings.weightKg || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="65"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="residenceCity"
                label="City"
                isPrivate={privacySettings.residenceCity || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Your city"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Background & Religion</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormFieldWithPrivacy
                control={form.control}
                name="religion"
                label="Religion"
                isPrivate={privacySettings.religion || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Your religion"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="casteOrSect"
                label="Caste/Sect"
                isPrivate={privacySettings.casteOrSect || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Optional"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="residenceCountry"
                label="Country"
                isPrivate={privacySettings.residenceCountry || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Your country"
              />
              
              <FormFieldWithPrivacy
                control={form.control}
                name="motherTongue"
                label="Mother Tongue"
                isPrivate={privacySettings.motherTongue || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Your mother tongue"
              />
            </div>
            
            <div className="space-y-4">
              <FormFieldWithPrivacy
                control={form.control}
                name="aboutMe"
                label="About Me"
                type="textarea"
                isPrivate={privacySettings.aboutMe || false}
                onPrivacyChange={handlePrivacyChange}
                placeholder="Write something about yourself"
              />
            </div>
          </CardContent>
        </Card>
      
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & Continue'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EnhancedPersonalInfoForm;